"use client";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { LongPressEventType, useLongPress } from "use-long-press";
import { useAccount, useReadContract } from "wagmi";

import TrackRenderer from "../abi/TrackRenderer";
import { RaceAd } from "../app/[s]/page";
import Stopwatch from "../components/Stopwatch";
import usePageVisibility from "../hooks/usePageVisibility";
import { trackEvent } from "../utils/analytics";
import { drawPath } from "../utils/drawPath";
import { currentTrack } from "../utils/getTheme";
import addStartingLine from "../utils/startingLine";
import PlayerPath from "./PlayerPath";
import ShareModal from "./ShareModal";

export type AppTouchEvent = TouchEvent;

function calcDistance(prevPoint, currPoint) {
  if (!prevPoint) {
    return 0;
  }

  const dx = currPoint[0] - prevPoint[0]; // difference in x-coordinates
  const dy = currPoint[1] - prevPoint[1]; // difference in y-coordinates

  // Square root of the sum of squared differences in x and y
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance;
}

const Track = ({
  track,
  seed,
  hideStopwatch,
  theme,
  ads,
}: {
  track: string;
  seed: string;
  hideStopwatch?: boolean;
  theme: any;
  ads: readonly RaceAd[];
}) => {
  const params = useParams();
  const router = useRouter();
  const [elapsed, setElapsed] = useState(0);
  const [completed, setCompleted] = useState(0);
  const { address, isConnected } = useAccount();
  const [gameState, setGameState] = useState<
    "ready" | "pole" | "start" | "playing" | "crashed" | "won"
  >("ready");

  useEffect(() => {
    if (params?.s) return;
    window.history.pushState({}, "", `/${seed}`);
  }, []);

  usePageVisibility((visible) => !visible && crash());

  const reset = () => {
    setWon(false);
    setGameState("ready");
    playerPath?.current?.setAttribute("d", "");
    points.current = [];

    passedMidway.current = false;
  };

  const [won, setWon] = useState(false);

  const [attempts, setAttempt] = useState(0);

  const points = useRef<Array<[number, number, number]>>([]);

  const passedMidway = useRef(false);

  const svg = useRef<SVGSVGElement>(null);
  const bg = useRef<SVGRectElement>(null);
  const road = useRef<SVGPathElement>(null);
  const finishLine = useRef<SVGRectElement>(null);
  const playerPath = useRef<SVGPathElement>(null);
  const crashBarrier = useRef<SVGPathElement>(null);
  const startArea = useRef<SVGPathElement>(null);
  const midwayGuard = useRef<SVGRectElement>(null);
  const clickWarning = useRef<SVGTextElement>(null);

  const showWarning = (e) => {
    clickWarning.current?.setAttribute("opacity", "1");
  };

  const bind = useLongPress(
    (e) => {
      console.log("huh");
      showWarning(e);
    },
    {
      onFinish: (e) => clickWarning.current?.setAttribute("opacity", "0"),
      detect: LongPressEventType.Mouse, // Default option
    }
  );

  useEffect(() => {
    const handleBlur = () => {
      if (gameState === "playing") {
        crash();
      }
    };

    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("blur", handleBlur);
    };
  }, [gameState]);

  useEffect(() => {
    const path = document.getElementById("track");
    startArea?.current?.setAttribute("d", track);

    path &&
      addStartingLine(path as unknown as SVGPathElement, startArea.current);
  }, [track]);

  const point = useMemo(() => svg?.current?.createSVGPoint(), [svg?.current]);

  const handleMove = (evt: React.MouseEvent | React.TouchEvent) => {
    if (!svg.current || !point || won) return;
    let x, y, finished;

    if (evt.nativeEvent instanceof MouseEvent) {
      x = evt.nativeEvent.clientX;
      y = evt.nativeEvent.clientY;
      finished = (p: DOMPointInit) => finishLine?.current?.isPointInFill(p);
    } else {
      x = evt.nativeEvent.touches[0].clientX;
      y = evt.nativeEvent.touches[0].clientY;
      finished = (p: DOMPointInit) => finishLine?.current?.isPointInFill(p);
    }

    const pt = new DOMPointReadOnly(x, y).matrixTransform(
      svg.current.getScreenCTM()?.inverse()
    );
    point.x = pt.x;
    point.y = pt.y;

    if (gameState === "ready") {
      if (startArea?.current?.isPointInStroke(point) && !finished?.(point)) {
        console.warn("pole");
        setGameState("pole");
        passedMidway.current = false;
        return;
      }
    }

    if (midwayGuard?.current?.isPointInFill(point)) {
      passedMidway.current = true;
    }

    if (finished?.(point)) {
      if (gameState === "ready") return;
      if (gameState === "pole") {
        console.warn("crossing start line");
        setGameState("start");
        return;
      }

      if (gameState === "playing") {
        if (passedMidway?.current !== true) {
          return crash();
        }
        console.warn("finish");
        setGameState("won");
        setWon(true);
        trackEvent("attempt", { status: "won", seed, isConnected, address });

        const d = drawPath(points.current);
      }
      return;
    }

    if (gameState === "start") {
      setGameState("playing");
      points.current = [[pt.x, pt.y, Date.now()]];
      console.warn("racing");
      setAttempt((curr) => curr + 1);
      return;
    }

    if (!road?.current?.isPointInStroke(point)) {
      if (gameState === "pole") return setGameState("ready");
      // points.current.push([pt.x, pt.y, Date.now()]);
      return crash();
    }

    if (
      calcDistance(points.current[points.current.length - 1], [pt.x, pt.y]) >
      200
    ) {
      return crash();
    }

    if (gameState !== "playing") return;
    points.current.push([pt.x, pt.y, Date.now()]);
    const _d = drawPath(points.current);
    playerPath?.current?.setAttribute("d", _d);
  };

  const crash = () => {
    if (gameState === "playing") {
      setGameState("crashed");
      console.warn("crash");
      const _d = drawPath(points.current);
      playerPath?.current?.setAttribute("d", _d);

      trackEvent("attempt", { status: "crash", seed, isConnected, address });
      return;
    }
  };

  const hardCrash = () => {
    setGameState("crashed");

    const _d = drawPath(points.current);
    playerPath?.current?.setAttribute("d", _d);

    trackEvent("attempt", { status: "crash", seed, isConnected, address });
  };

  const checkerSize = 12;

  const { data: rrAd } = useReadContract({
    ...TrackRenderer,
    address: TrackRenderer.rendererAddress(Number(seed)),
    functionName: "raceResultAds",
    query: {
      enabled: Boolean(seed),
    },
    args: [BigInt(seed || 0)],
  });

  return (
    <div {...bind()} onContextMenu={hardCrash}>
      <svg
        ref={svg}
        className="absolute top-0 left-0 w-full h-full select-none"
        style={{ overflow: "inherit" }}
        id="canvas"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        viewBox="0 0 1200 1950"
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onTouchStart={handleMove}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          crash();
        }}
      >
        <defs>
          <pattern
            id="pattern-checkers"
            x="0"
            y="0"
            width={checkerSize * 2}
            height={checkerSize * 2}
            patternUnits="userSpaceOnUse"
            fill="white"
            transform="rotate(270)"
          >
            <rect
              className="checker"
              x="0"
              width={checkerSize}
              height={checkerSize}
              y="0"
            ></rect>
            <rect
              className="checker"
              x={checkerSize}
              width={checkerSize}
              height={checkerSize}
              y={checkerSize}
            ></rect>
          </pattern>

          <filter
            id="noise"
            filterUnits="objectBoundingBox"
            colorInterpolationFilters="sRGB"
          >
            {["Mono", "Ringers", "Coinbase"].includes(theme.name) ? (
              <feComponentTransfer>
                <feFuncR type="identity" />
                <feFuncG type="identity" />
                <feFuncB type="identity" />
                <feFuncA type="identity" />
              </feComponentTransfer>
            ) : (
              <>
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.25"
                  numOctaves="2"
                  result="noise"
                />
                <feComposite in="SourceGraphic" in2="noise" operator="in" />
              </>
            )}
          </filter>
        </defs>
        <rect
          ref={bg}
          width="100%"
          height="100%"
          fill={theme.background}
        ></rect>

        {ads?.map(({ src, rotation, ...pos }, i) => (
          <foreignObject
            key={i}
            {...pos}
            x={pos.x}
            y={pos.y}
            transform={`rotate(${rotation} ${pos.x + pos.width / 2} ${
              pos.y + pos.height / 2
            }) `}
          >
            <Image
              priority
              placeholder="blur"
              blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              loading="eager"
              alt="ad-image"
              width={pos.width}
              height={pos.height}
              style={{
                objectFit: "contain",
                pointerEvents: "none",
                touchAction: "none",
                userSelect: "none",
              }}
              src={src.replace(
                "https://ipfs.io/",
                "https://cloudflare-ipfs.com/"
              )}
            />
          </foreignObject>
        ))}
        <path
          id="x"
          ref={crashBarrier}
          strokeLinecap="round"
          fill="none"
          stroke={theme.crashBarrier}
          strokeWidth="100"
          strokeLinejoin="round"
          opacity={1}
          d={track}
        ></path>
        <path
          id="track"
          ref={road}
          strokeLinecap="round"
          fill="none"
          stroke={theme.road}
          strokeWidth="88"
          strokeLinejoin="round"
          opacity={1}
          filter="url(#noise)"
          d={track}
        ></path>
        <path
          id="mid"
          strokeLinecap="round"
          fill="none"
          stroke={theme.dash}
          strokeDasharray={theme.strokeDashArray}
          strokeWidth="4"
          strokeLinejoin="round"
          opacity={1}
          d={track}
        ></path>
        {track ? (
          <>
            <text
              x="600"
              y="100"
              textAnchor="middle"
              ref={clickWarning}
              opacity={0}
              fontSize={36}
              fill="red"
              style={{ transition: "opacity 0.5s ease-in-out" }}
            >
              You don&apos;t need to click, just trace!
            </text>
            <path
              x="-100"
              y="-100"
              // id="startArea"
              stroke="#FFFF00"
              strokeWidth="88"
              strokeLinejoin="round"
              opacity={["pole", "ready"].includes(gameState) ? 0.75 : 0}
              fill="none"
              ref={startArea}
              // d={track}
            ></path>
            <text
              x="-100"
              y="-100"
              id="startText"
              textAnchor="middle"
              fontSize="46px"
              fill="black"
              opacity={["pole", "ready"].includes(gameState) ? 1 : 0}
            >
              <animate
                attributeName="font-size"
                attributeType="XML"
                values="42;54;42;"
                repeatCount="indefinite"
                begin="0s"
                dur="3s"
              />
              {gameState === "pole" ? "GO" : "START"} &rarr;
            </text>

            <rect
              x="-100"
              y="-100"
              id="line-bg"
              width="18px"
              height="56px"
              fill="white"
              opacity={1}
            ></rect>
            <rect
              x="-100"
              y="-100"
              ref={finishLine}
              id="line"
              width="18px"
              height="56px"
              fill="url(#pattern-checkers)"
              stroke="black"
              opacity={1}
            ></rect>
            <rect
              x="-100"
              y="-100"
              ref={midwayGuard}
              id="mid-guard"
              fill="none"
              opacity={1}
            ></rect>
            <PlayerPath
              key={seed}
              crashed={gameState === "crashed"}
              points={points.current}
              ref={playerPath}
              attempt={attempts}
              theme={theme}
            />
          </>
        ) : null}
      </svg>

      {hideStopwatch ? null : (
        <Stopwatch
          key={track}
          playerPath={playerPath.current}
          track={road.current}
          gameState={gameState}
          seed={seed}
          onComplete={(_elapsed: number, _completed: number) => {
            setElapsed(_elapsed);
            setCompleted(_completed);
          }}
        />
      )}
      {gameState === "crashed" || gameState === "won" ? (
        <ShareModal
          adHref={rrAd?.[1]}
          adSrc={rrAd?.[0]}
          attempts={attempts}
          completed={completed}
          elapsed={
            points.current.length
              ? (points.current[points.current.length - 1][2] -
                  points.current[0][2]) /
                1000
              : 0
          }
          gameState={gameState}
          pathPoints={points.current}
          track={track}
          reset={reset}
          seed={seed}
          theme={theme}
          show={gameState === "crashed" || gameState === "won"}
        />
      ) : null}
    </div>
  );
};

export default Track;
